/**
 * @fileoverview NIP-11: what a relay says about itself, and the one field the
 * publish path cannot do without.
 *
 * WHY THIS EXISTS AT ALL. A relay enforces a maximum websocket message size,
 * and an oversized message is refused by CLOSING THE CONNECTION -- no `OK
 * false`, no `NOTICE`, nothing a client can read. The library surfaces that as
 * a generic socket failure, indistinguishable from a network drop. An author
 * whose page is too big would see "something went wrong", see it forever, and
 * have no reason to suspect the page rather than their wifi.
 *
 * So the publish path measures the message BEFORE sending it and refuses
 * locally, naming the limit. That requires knowing the limit, and this is where
 * it comes from.
 *
 * IT IS READ PER RELAY AND NEVER ASSUMED FLEET-WIDE. The ceiling is a relay's
 * own configuration. Publishing to someone else's relay with our relay's number
 * in hand would be a guess dressed as a check.
 *
 * MEASURED 2026-09-15 against relay.cloistr.xyz (software 0.6.0): it now serves
 * `limitation.max_message_length: 512000`, which matches the value that was
 * found by bisection when the document carried no limitation object at all.
 * Advertised and enforced agree. That is worth stating because it has not
 * always been true -- the field was committed before it was served.
 *
 * WE READ EXACTLY ONE FIELD AND IGNORE THE REST, and the reason is narrower
 * than an earlier draft of this comment claimed. That draft called the document
 * "not uniformly reliable" because it advertises `auth_required: true` while the
 * relay looked open to strangers. THAT WAS WRONG, and it is corrected here
 * rather than quietly deleted, because the misreading is an easy one to repeat:
 * open to any key and mandatory sign-in are not in conflict. Reads need nothing.
 * Writes need the NIP-42 handshake, from anyone, with no allow-list. The
 * advertised value is correct and `publishEvent` completes the handshake.
 *
 * So the single field is read because it is the single field this module is
 * for. A ceiling is also the safe kind of value to take from a document someone
 * else writes: we enforce it ourselves and conservatively, so the worst a wrong
 * number can do is refuse a page that would have fit -- which the author sees,
 * and can act on.
 */

/**
 * The ceiling assumed for a relay that advertises none.
 *
 * NOT an arbitrary round number. It is khatru's default `MaxMessageSize`, which
 * the great majority of relays inherit without ever choosing it -- including
 * ours, which is how we ended up measuring it by bisection before it was
 * advertised. A relay that has thought about the limit advertises it and this
 * value is never used; a relay that has not is overwhelmingly likely to be
 * sitting on exactly this.
 */
export const DEFAULT_MAX_MESSAGE_BYTES = 512_000;

/** How long to wait for a relay's information document. */
const INFO_TIMEOUT_MS = 5_000;

export interface RelayLimits {
  /** Maximum websocket message size in bytes. */
  maxMessageBytes: number;
  /** False when the relay advertised nothing and the default is standing in. */
  advertised: boolean;
}

/**
 * Cached per relay for the lifetime of the page.
 *
 * A relay's ceiling does not change between two publishes a minute apart, and
 * an HTTP round trip before every publish would be a visible pause on the one
 * action where the author is already waiting on a signer.
 */
const cache = new Map<string, RelayLimits>();

/** `wss://relay.example/` -> `https://relay.example` */
function infoUrl(relay: string): string | null {
  try {
    const url = new URL(relay);
    if (url.protocol === 'wss:') url.protocol = 'https:';
    else if (url.protocol === 'ws:') url.protocol = 'http:';
    else return null;
    return url.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * Ask a relay for its limits.
 *
 * NEVER THROWS AND NEVER LEAVES THE CALLER WITHOUT A NUMBER. A relay that is
 * unreachable over HTTP may still be reachable over websocket -- they are
 * different ports on some deployments and different proxies on others -- so a
 * failed information fetch must not block publishing. It falls back to the
 * default and says so, and the caller decides how loudly to mention it.
 */
export async function fetchRelayLimits(relay: string): Promise<RelayLimits> {
  const cached = cache.get(relay);
  if (cached) return cached;

  const fallback: RelayLimits = {
    maxMessageBytes: DEFAULT_MAX_MESSAGE_BYTES,
    advertised: false,
  };

  const url = infoUrl(relay);
  if (!url) return fallback;

  let limits = fallback;
  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/nostr+json' },
      signal: AbortSignal.timeout(INFO_TIMEOUT_MS),
    });
    if (response.ok) {
      limits = readLimits(await response.json()) ?? fallback;
    }
  } catch {
    // Unreachable, timed out, blocked by CORS, or not JSON. All the same
    // outcome: we do not know, so we assume the common default.
  }

  cache.set(relay, limits);
  return limits;
}

/**
 * Pull the one field out, refusing anything that is not a usable positive
 * integer.
 *
 * A relay information document is a remote JSON blob written by someone else.
 * A `max_message_length` of `"lots"`, of `0`, or of `-1` must land in the same
 * place as an absent one rather than becoming a ceiling that refuses every page
 * or accepts every page.
 */
function readLimits(doc: unknown): RelayLimits | null {
  if (typeof doc !== 'object' || doc === null) return null;
  const limitation = (doc as Record<string, unknown>).limitation;
  if (typeof limitation !== 'object' || limitation === null) return null;

  const value = (limitation as Record<string, unknown>).max_message_length;
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;

  return { maxMessageBytes: Math.floor(value), advertised: true };
}

/** Testing seam: the cache is process-wide and would leak between cases. */
export function clearRelayLimitsCache(): void {
  cache.clear();
}
